    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Rostering" title="Roster and Shift Planning" subtitle="Plan workforce coverage, assign shifts, review readiness conflicts, and publish rosters into time generation.">
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()" [disabled]="loading"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'plans', label: 'Roster Plans', icon: 'fa-solid fa-calendar-days', badge: plans.length },
          { key: 'assignments', label: 'Assignments', icon: 'fa-solid fa-user-clock', badge: assignments.length },
          { key: 'patterns', label: 'Weekly Patterns', icon: 'fa-solid fa-calendar-week', badge: workPatterns.length },
          { key: 'conflicts', label: 'Conflicts', icon: 'fa-solid fa-triangle-exclamation', badge: conflicts.length },
          { key: 'coverage', label: 'Coverage', icon: 'fa-solid fa-clipboard-list', badge: coverage.length },
          { key: 'calendar', label: 'Roster Calendar', icon: 'fa-solid fa-table-cells-large', badge: calendarDays.length },
          { key: 'history', label: 'Change Log', icon: 'fa-solid fa-clock-rotate-left', badge: changeLog.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="roster-layout">
        <app-hr-action-dialog [visible]="actionDialogVisible('plan')" (visibleChange)="setActionDialogVisible('plan', $event)" [title]="planForm.rosterPlanId ? 'Edit Roster Plan' : 'New Roster Plan'" eyebrow="Roster Planning" subtitle="Create the planning window before assigning workforce and coverage." icon="fa-solid fa-calendar-plus" [contextTitle]="planForm.planName || 'New roster plan'" [contextSubtitle]="planForm.planCode || 'Code required'">
        <app-page-form-section icon="fa-solid fa-calendar-plus" title="Roster Plan" subtitle="Create the weekly planning shell before adding assignments and coverage.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="planCode" pInputText [(ngModel)]="planForm.planCode" />
                <label for="planCode">Plan code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-signature"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="planName" pInputText [(ngModel)]="planForm.planName" />
                <label for="planName">Plan name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="periodStart" pInputText type="date" [(ngModel)]="planForm.periodStart" />
                <label for="periodStart">Period start</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="periodEnd" pInputText type="date" [(ngModel)]="planForm.periodEnd" />
                <label for="periodEnd">Period end</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="businessUnit" pInputText [(ngModel)]="planForm.businessUnit" />
                <label for="businessUnit">Business unit</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-flag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="planStatus" [options]="planStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="planForm.planStatus" styleClass="w-full"></p-select>
                <label for="planStatus">Status</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="planNotes" pTextarea rows="3" [(ngModel)]="planForm.notes"></textarea>
                <label for="planNotes">Notes</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="form-actions">
            <button pButton type="button" icon="fa-solid fa-save" label="Save Plan" (click)="savePlan()" [disabled]="!canManageRoster || !planForm.planCode || !planForm.planName || !planForm.periodStart || !planForm.periodEnd"></button>
            <button pButton type="button" icon="pi pi-undo" label="New" severity="secondary" [outlined]="true" (click)="resetPlan()"></button>
          </div>
        </app-page-form-section>
        </app-hr-action-dialog>

        <app-page-data-panel [hidden]="activeListTab !== 'plans'" title="Roster Plans" subtitle="Select a plan to manage assignments, coverage, conflicts, and publish state." [resultText]="plans.length + ' plans'">
          @if (canManageRoster) {
            <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Plan" (click)="openActionDialog('plan')"></button></div>
          }
          <p-table [value]="plans" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '880px' }">
            <ng-template pTemplate="header">
              <tr>
                <th>Plan</th>
                <th>Period</th>
                <th>Status</th>
                <th>Assignments</th>
                <th>Conflicts</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan>
              <tr [class.selected-row]="plan.rosterPlanId === selectedPlan?.rosterPlanId">
                <td><strong>{{ plan.planName }}</strong><div class="muted">{{ plan.planCode }}</div></td>
                <td>{{ plan.periodStart }} to {{ plan.periodEnd }}</td>
                <td><p-tag [value]="format(plan.planStatus)" [severity]="planSeverity(plan.planStatus)"></p-tag></td>
                <td>{{ plan.assignmentCount || 0 }}</td>
                <td><p-tag [value]="(plan.openConflictCount || 0) + ' open'" [severity]="(plan.openConflictCount || 0) > 0 ? 'danger' : 'success'"></p-tag></td>
                <td class="row-actions">
                  <button pButton type="button" icon="fa-solid fa-folder-open" label="Open" size="small" (click)="selectPlan(plan)"></button>
                  <button pButton type="button" icon="fa-solid fa-pen" label="Edit" size="small" severity="secondary" [outlined]="true" (click)="editPlan(plan)" [disabled]="!canManageRoster || plan.planStatus === 'PUBLISHED'"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      @if (selectedPlan) {
        <section class="selected-plan">
          <app-page-data-panel [hidden]="activeListTab !== 'plans'" title="Selected Plan" [subtitle]="selectedPlan.planName || ''" [resultText]="selectedPlan.planStatus || 'DRAFT'">
            <div actions class="selected-actions">
              <button pButton type="button" icon="fa-solid fa-triangle-exclamation" label="Recalculate Conflicts" severity="secondary" [outlined]="true" (click)="recalculateConflicts()" [disabled]="!canManageRoster"></button>
              <button pButton type="button" icon="fa-solid fa-wand-magic-sparkles" label="Generate From Patterns" severity="secondary" [outlined]="true" (click)="generateFromPatterns()" [disabled]="!canManageRoster || selectedPlan.planStatus === 'PUBLISHED'"></button>
              <button pButton type="button" icon="fa-solid fa-upload" label="Publish" (click)="publishPlan()" [disabled]="!canPublishRoster || selectedPlan.planStatus === 'PUBLISHED' || hasBlockers"></button>
              <button pButton type="button" icon="fa-solid fa-rotate-left" label="Unpublish" severity="secondary" [outlined]="true" (click)="unpublishPlan()" [disabled]="!canPublishRoster || selectedPlan.planStatus !== 'PUBLISHED'"></button>
            </div>
          </app-page-data-panel>
        </section>

        <section class="roster-layout">
          <app-hr-action-dialog [visible]="actionDialogVisible('assignment')" (visibleChange)="setActionDialogVisible('assignment', $event)" [title]="assignmentForm.rosterAssignmentId ? 'Edit Assignment' : 'New Assignment'" eyebrow="Roster Assignment" subtitle="Assign an employee to a dated shift with optional operational references." icon="fa-solid fa-user-clock" [contextTitle]="selectedPlan.planName || 'Selected plan'" [contextSubtitle]="assignmentForm.workDate || 'Work date required'">
          <app-page-form-section icon="fa-solid fa-user-clock" title="Assignment" subtitle="Assign employees to dated shifts and link operational work where applicable.">
            <div class="form-grid">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="assignmentEmployee" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="assignmentForm.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                  <label for="assignmentEmployee">Employee</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="assignmentType" [options]="assignmentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="assignmentForm.assignmentType" styleClass="w-full"></p-select>
                  <label for="assignmentType">Assignment type</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="workDate" pInputText type="date" [(ngModel)]="assignmentForm.workDate" />
                  <label for="workDate">Work date</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="assignmentStart" pInputText type="datetime-local" [(ngModel)]="assignmentForm.startTime" />
                  <label for="assignmentStart">Start time</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="assignmentEnd" pInputText type="datetime-local" [(ngModel)]="assignmentForm.endTime" />
                  <label for="assignmentEnd">End time</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-certificate"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="assignmentSkill" pInputText [(ngModel)]="assignmentForm.requiredSkillCode" />
                  <label for="assignmentSkill">Required skill</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="assignmentLocation" pInputText [(ngModel)]="assignmentForm.locationName" />
                  <label for="assignmentLocation">Location</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="assignmentResource" pInputText type="number" [(ngModel)]="resourceId" />
                  <label for="assignmentResource">Primary resource ID</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="form-actions">
              <button pButton type="button" icon="fa-solid fa-save" label="Save Assignment" (click)="saveAssignment()" [disabled]="!canManageRoster || selectedPlan.planStatus === 'PUBLISHED' || !assignmentForm.employeeId || !assignmentForm.assignmentType || !assignmentForm.startTime || !assignmentForm.endTime"></button>
              <button pButton type="button" icon="pi pi-undo" label="New" severity="secondary" [outlined]="true" (click)="resetAssignment()"></button>
            </div>
          </app-page-form-section>
          </app-hr-action-dialog>

          <app-page-data-panel [hidden]="activeListTab !== 'assignments'" title="Assignments" subtitle="Published assignments become planned timesheet lines when attendance is absent." [resultText]="assignments.length + ' assignments'">
            @if (canManageRoster) {
              <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Assignment" (click)="openActionDialog('assignment')" [disabled]="!selectedPlan"></button></div>
            }
            <p-table [value]="assignments" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1040px' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Window</th>
                  <th>Hours</th>
                  <th>Status</th>
                  <th>Resource</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-assignment>
                <tr>
                  <td><strong>{{ assignment.employeeName || assignment.employeeId }}</strong><div class="muted">{{ assignment.employeeNumber || assignment.workerCategory || '-' }}</div></td>
                  <td>{{ format(assignment.assignmentType) }}</td>
                  <td>{{ assignment.startTime | date:'short' }}<div class="muted">{{ assignment.endTime | date:'short' }}</div></td>
                  <td>{{ assignment.plannedHours || 0 }}</td>
                  <td><p-tag [value]="format(assignment.assignmentStatus)" [severity]="assignment.assignmentStatus === 'PUBLISHED' ? 'success' : 'secondary'"></p-tag></td>
                  <td>{{ resourceLabel(assignment) }}</td>
                  <td class="row-actions">
                    <button pButton type="button" icon="fa-solid fa-pen" label="Edit" size="small" severity="secondary" [outlined]="true" (click)="editAssignment(assignment)" [disabled]="!canManageRoster || selectedPlan?.planStatus === 'PUBLISHED'"></button>
                    <button pButton type="button" icon="fa-solid fa-trash" label="Delete" size="small" severity="danger" [outlined]="true" (click)="deleteAssignment(assignment)" [disabled]="!canManageRoster || selectedPlan?.planStatus === 'PUBLISHED'"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </app-page-data-panel>
        </section>

        <section class="roster-layout">
          <app-hr-action-dialog [visible]="actionDialogVisible('template')" (visibleChange)="setActionDialogVisible('template', $event)" [title]="templateForm.shiftTemplateId ? 'Edit Shift Template' : 'New Shift Template'" eyebrow="Roster Setup" subtitle="Maintain reusable shift times and worker classification." icon="fa-solid fa-sliders" [contextTitle]="templateForm.templateName || 'New shift template'" [contextSubtitle]="templateForm.templateCode || 'Code required'">
          <app-page-form-section icon="fa-solid fa-sliders" title="Shift Template" subtitle="Reusable shift definitions for assignment planning.">
            <div class="form-grid">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="templateCode" pInputText [(ngModel)]="templateForm.templateCode" />
                  <label for="templateCode">Code</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-signature"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="templateName" pInputText [(ngModel)]="templateForm.templateName" />
                  <label for="templateName">Name</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="templateType" [options]="assignmentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="templateForm.assignmentType" styleClass="w-full"></p-select>
                  <label for="templateType">Type</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-users"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="templateWorkerCategory" [options]="workerCategoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="templateForm.workerCategory" styleClass="w-full"></p-select>
                  <label for="templateWorkerCategory">Worker category</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="templateStart" pInputText type="time" [(ngModel)]="templateForm.startTime" />
                  <label for="templateStart">Start</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="templateEnd" pInputText type="time" [(ngModel)]="templateForm.endTime" />
                  <label for="templateEnd">End</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="form-actions">
              <button pButton type="button" icon="fa-solid fa-save" label="Save Template" (click)="saveTemplate()" [disabled]="!canManageRoster || !templateForm.templateCode || !templateForm.templateName || !templateForm.assignmentType || !templateForm.startTime || !templateForm.endTime"></button>
              <button pButton type="button" icon="pi pi-undo" label="New" severity="secondary" [outlined]="true" (click)="resetTemplate()"></button>
            </div>
          </app-page-form-section>
          </app-hr-action-dialog>

          <app-hr-action-dialog [visible]="actionDialogVisible('pattern')" (visibleChange)="setActionDialogVisible('pattern', $event)" [title]="workPatternForm.workPatternId ? 'Edit Work Pattern' : 'New Work Pattern'" eyebrow="Roster Setup" subtitle="Define the employee's recurring weekly schedule." icon="fa-solid fa-calendar-week" [contextTitle]="workPatternForm.employeeId ? 'Employee ' + workPatternForm.employeeId : 'Employee required'" [contextSubtitle]="weekdayLabel(workPatternForm.dayOfWeek)">
          <app-page-form-section icon="fa-solid fa-calendar-week" title="Weekly Work Pattern" subtitle="Default employee schedules that generate dated roster assignments.">
            <div class="form-grid">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="patternEmployee" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="workPatternForm.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                  <label for="patternEmployee">Employee</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="patternDay" [options]="weekdayOptions" optionLabel="label" optionValue="value" [(ngModel)]="workPatternForm.dayOfWeek" styleClass="w-full"></p-select>
                  <label for="patternDay">Weekday</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-sliders"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="patternTemplate" [options]="templateOptions" optionLabel="label" optionValue="value" [(ngModel)]="workPatternForm.shiftTemplateId" styleClass="w-full"></p-select>
                  <label for="patternTemplate">Template</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="patternType" [options]="assignmentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="workPatternForm.assignmentType" styleClass="w-full"></p-select>
                  <label for="patternType">Type</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="patternStart" pInputText type="time" [(ngModel)]="workPatternForm.startTime" />
                  <label for="patternStart">Start</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="patternEnd" pInputText type="time" [(ngModel)]="workPatternForm.endTime" />
                  <label for="patternEnd">End</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="patternEffectiveFrom" pInputText type="date" [(ngModel)]="workPatternForm.effectiveFrom" />
                  <label for="patternEffectiveFrom">Effective from</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="patternLocation" pInputText [(ngModel)]="workPatternForm.locationName" />
                  <label for="patternLocation">Location</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="form-actions">
              <button pButton type="button" icon="fa-solid fa-save" label="Save Pattern" (click)="saveWorkPattern()" [disabled]="!canManageRoster || !workPatternForm.employeeId || !workPatternForm.dayOfWeek || !workPatternForm.assignmentType || !workPatternForm.startTime || !workPatternForm.endTime || !workPatternForm.effectiveFrom"></button>
              <button pButton type="button" icon="pi pi-undo" label="New" severity="secondary" [outlined]="true" (click)="resetWorkPattern()"></button>
            </div>
          </app-page-form-section>
          </app-hr-action-dialog>
        </section>

        <section class="roster-layout">
          <app-hr-action-dialog [visible]="actionDialogVisible('coverage')" (visibleChange)="setActionDialogVisible('coverage', $event)" title="Coverage Requirement" eyebrow="Roster Coverage" subtitle="Capture expected headcount by date, duty, location, and skill." icon="fa-solid fa-clipboard-list" [contextTitle]="selectedPlan.planName || 'Selected plan'" [contextSubtitle]="coverageForm.requirementDate || 'Coverage date required'">
          <app-page-form-section icon="fa-solid fa-clipboard-list" title="Coverage Requirement" subtitle="Capture expected headcount by date, duty type, location, and skill.">
            <div class="form-grid">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="coverageDate" pInputText type="date" [(ngModel)]="coverageForm.requirementDate" />
                  <label for="coverageDate">Date</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="coverageType" [options]="assignmentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="coverageForm.assignmentType" styleClass="w-full"></p-select>
                  <label for="coverageType">Type</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-users"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="coverageCount" pInputText type="number" [(ngModel)]="coverageForm.requiredCount" />
                  <label for="coverageCount">Required count</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="coverageLocation" pInputText [(ngModel)]="coverageForm.locationName" />
                  <label for="coverageLocation">Location</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="form-actions">
              <button pButton type="button" icon="fa-solid fa-save" label="Save Coverage" (click)="saveCoverage()" [disabled]="!canManageRoster || selectedPlan.planStatus === 'PUBLISHED' || !coverageForm.requirementDate || !coverageForm.assignmentType"></button>
              <button pButton type="button" icon="pi pi-undo" label="New" severity="secondary" [outlined]="true" (click)="resetCoverage()"></button>
            </div>
          </app-page-form-section>
          </app-hr-action-dialog>

          <app-page-data-panel [hidden]="activeListTab !== 'patterns'" title="Weekly Patterns" subtitle="Active defaults used to generate roster rows for the selected plan." [resultText]="workPatterns.length + ' patterns'">
            @if (canManageRoster) {
              <div actions>
                <button pButton type="button" icon="fa-solid fa-plus" label="New Work Pattern" (click)="openActionDialog('pattern')"></button>
                <button pButton type="button" icon="fa-solid fa-sliders" label="Shift Template" severity="secondary" outlined (click)="openActionDialog('template')"></button>
              </div>
            }
            <p-table [value]="workPatterns" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '820px' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>Employee</th>
                  <th>Day</th>
                  <th>Window</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-pattern>
                <tr>
                  <td><strong>{{ pattern.employeeName || pattern.employeeId }}</strong><div class="muted">{{ pattern.employeeNumber || pattern.workerCategory || '-' }}</div></td>
                  <td>{{ weekdayLabel(pattern.dayOfWeek) }}</td>
                  <td>{{ pattern.startTime }} to {{ pattern.endTime }}<div class="muted">{{ pattern.effectiveFrom }}{{ pattern.effectiveUntil ? ' to ' + pattern.effectiveUntil : '' }}</div></td>
                  <td>{{ format(pattern.assignmentType) }}</td>
                  <td><p-tag [value]="pattern.active === false ? 'Inactive' : 'Active'" [severity]="pattern.active === false ? 'secondary' : 'success'"></p-tag></td>
                  <td class="row-actions">
                    <button pButton type="button" icon="fa-solid fa-pen" label="Edit" size="small" severity="secondary" [outlined]="true" (click)="editWorkPattern(pattern)" [disabled]="!canManageRoster"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </app-page-data-panel>
        </section>

        <section class="roster-layout">
          <app-page-data-panel [hidden]="activeListTab !== 'conflicts'" title="Conflicts" subtitle="Leave, readiness, overlap, rest, skill, and operational-resource issues detected before publish." [resultText]="conflicts.length + ' conflicts'">
            <p-table [value]="conflicts" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '880px' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>Severity</th>
                  <th>Type</th>
                  <th>Employee</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-conflict>
                <tr>
                  <td><p-tag [value]="conflict.severity" [severity]="conflict.severity === 'BLOCKER' ? 'danger' : 'warn'"></p-tag></td>
                  <td>{{ format(conflict.conflictType) }}</td>
                  <td>{{ conflict.employeeName || conflict.employeeId || '-' }}</td>
                  <td>{{ conflict.description }}</td>
                  <td>{{ conflict.conflictStatus }}</td>
                </tr>
              </ng-template>
            </p-table>
          </app-page-data-panel>

          <app-page-data-panel [hidden]="activeListTab !== 'coverage'" title="Coverage" subtitle="Required coverage rows for the selected roster plan." [resultText]="coverage.length + ' rows'">
            @if (canManageRoster) {
              <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Coverage" (click)="openActionDialog('coverage')" [disabled]="!selectedPlan"></button></div>
            }
            <p-table [value]="coverage" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '760px' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Count</th>
                  <th>Skill</th>
                  <th>Location</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.requirementDate }}</td>
                  <td>{{ format(row.assignmentType) }}</td>
                  <td>{{ row.requiredCount || 1 }}</td>
                  <td>{{ row.requiredSkillCode || '-' }}</td>
                  <td>{{ row.locationName || '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </app-page-data-panel>
        </section>

        <section class="roster-layout">
          <app-page-data-panel [hidden]="activeListTab !== 'calendar'" title="Roster Calendar" subtitle="Daily grid of planned assignments across the selected roster window." [resultText]="calendarDays.length + ' days'">
            <div class="calendar-grid">
              @for (day of calendarDays; track day.date) {
                <article class="calendar-day">
                  <header>
                    <strong>{{ day.label }}</strong>
                    <small>{{ day.rows.length }} assignments</small>
                  </header>
                  @if (day.rows.length) {
                    <div class="calendar-items">
                      @for (row of day.rows; track row.rosterAssignmentId || ($index + '-' + row.employeeId)) {
                        <div class="calendar-item">
                          <strong>{{ row.employeeName || row.employeeId }}</strong>
                          <span>{{ format(row.assignmentType) }}</span>
                          <small>{{ row.startTime | date:'shortTime' }} - {{ row.endTime | date:'shortTime' }}</small>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="muted">No assignments planned.</p>
                  }
                </article>
              }
            </div>
          </app-page-data-panel>

          <app-page-data-panel [hidden]="activeListTab !== 'history'" title="Change Log" subtitle="Audit trail for plan edits, coverage updates, generation, and publish actions." [resultText]="changeLog.length + ' entries'">
            <p-table [value]="changeLog" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '860px' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Summary</th>
                  <th>Actor</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-entry>
                <tr>
                  <td>{{ entry.createdAt | date:'short' }}</td>
                  <td>{{ format(entry.changeType) }}</td>
                  <td><strong>{{ entry.summary || '-' }}</strong><div class="muted">{{ entry.details || '' }}</div></td>
                  <td>{{ entry.createdByCustomerId || 'System' }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="4">No roster changes logged yet.</td></tr>
              </ng-template>
            </p-table>
          </app-page-data-panel>
        </section>
      }
    </app-page-shell>
